import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
// Icons removed - no icons used in UI

interface DataEncodingsControlsProps {
  plotlyJson?: any;
  onUpdate: (updateType: string, value: any) => void;
}

// Helper to deep clone a figure
function cloneFigure(figure: any): any {
  return JSON.parse(JSON.stringify(figure));
}

// Helper to get original data from a trace (stored in _originalData or use current)
function getOriginalTraceData(trace: any): { x?: any[]; y?: any[] } {
  if ((trace as any)._originalData) {
    return (trace as any)._originalData;
  }
  return { x: trace.x, y: trace.y };
}

// Helper to store original data in a trace
function storeOriginalTraceData(trace: any) {
  if (!(trace as any)._originalData) {
    (trace as any)._originalData = {
      x: Array.isArray(trace.x) ? [...trace.x] : trace.x,
      y: Array.isArray(trace.y) ? [...trace.y] : trace.y,
    };
  }
}

// Helper to check if a trace is empty after filtering
function isTraceEmpty(trace: any): boolean {
  if (!trace) return true;
  
  // Check for standard x/y data
  const hasX = Array.isArray(trace.x) && trace.x.length > 0;
  const hasY = Array.isArray(trace.y) && trace.y.length > 0;
  const hasZ = Array.isArray(trace.z) && trace.z.length > 0;
  
  // Check for other trace types
  const hasR = Array.isArray(trace.r) && trace.r.length > 0;
  const hasTheta = Array.isArray(trace.theta) && trace.theta.length > 0;
  const hasValues = Array.isArray(trace.values) && trace.values.length > 0;
  const hasLabels = Array.isArray(trace.labels) && trace.labels.length > 0;
  const hasSankeyNodes = Array.isArray(trace.node?.label) && trace.node.label.length > 0 || 
                        (Array.isArray(trace.node?.labels) && trace.node.labels.length > 0);
  const hasSankeyLinks = Array.isArray(trace.link?.value) && trace.link.value.length > 0;
  const hasIndicatorValue = (trace.type === 'indicator' || trace.mode === 'gauge' || trace.mode === 'number') && 
                           (typeof trace.value === 'number' && !isNaN(trace.value) && isFinite(trace.value));
  
  return !hasX && !hasY && !hasZ && !(hasR && hasTheta) && !(hasValues && hasLabels) && 
         !(hasSankeyNodes && hasSankeyLinks) && !hasIndicatorValue;
}

export function DataEncodingsControls({ plotlyJson, onUpdate }: DataEncodingsControlsProps) {
  const traces = plotlyJson?.data || [];
  const layout = plotlyJson?.layout || {};
  
  // Store original figure for reversible operations
  const originalFigureRef = useRef<any>(null);
  
  // Store original figure when plotlyJson first loads or when data structure changes significantly
  useEffect(() => {
    if (plotlyJson && plotlyJson.data && Array.isArray(plotlyJson.data)) {
      // Only update if we don't have an original yet, or if the number of traces changed
      // (indicating a new plot was loaded)
      const currentTraceCount = plotlyJson.data.length;
      const originalTraceCount = originalFigureRef.current?.data?.length || 0;
      
      if (!originalFigureRef.current || currentTraceCount !== originalTraceCount) {
        originalFigureRef.current = cloneFigure(plotlyJson);
        // Store original data for all traces
        (originalFigureRef.current.data || []).forEach((trace: any) => {
          storeOriginalTraceData(trace);
        });
      }
    }
  }, [plotlyJson?.data?.length]); // Update when trace count changes (new plot loaded)

  // Series visibility state
  const [seriesVisibility, setSeriesVisibility] = useState<Record<number, boolean>>(() => {
    const vis: Record<number, boolean> = {};
    traces.forEach((trace: any, idx: number) => {
      vis[idx] = trace.visible !== false && trace.visible !== 'legendonly';
    });
    return vis;
  });

  // Series order state
  const [seriesOrder, setSeriesOrder] = useState<number[]>(() => 
    traces.map((_: any, idx: number) => idx)
  );

  // Orientation state (for bar/box/violin) - detect from traces
  const [orientation, setOrientation] = useState<'v' | 'h'>(() => {
    const barTraces = traces.filter((t: any) => ['bar', 'box', 'violin'].includes(t.type));
    if (barTraces.length > 0) {
      return barTraces[0].orientation === 'h' ? 'h' : 'v';
    }
    return 'v';
  });

  // Sync orientation state when traces change
  useEffect(() => {
    const barTraces = traces.filter((t: any) => ['bar', 'box', 'violin'].includes(t.type));
    if (barTraces.length > 0) {
      const currentOrientation = barTraces[0].orientation === 'h' ? 'h' : 'v';
      setOrientation(currentOrientation);
    }
  }, [traces]);

  // Category order state
  const [categoryOrder, setCategoryOrder] = useState<{
    x?: { mode: string; custom?: string[] };
    y?: { mode: string; custom?: string[] };
  }>({});

  // Histogram binning state
  const [histogramBins, setHistogramBins] = useState<Record<number, {
    size?: number;
    start?: number;
    end?: number;
    norm?: string;
  }>>({});

  // Filter state
  const [filters, setFilters] = useState<{
    categories?: Record<string, string[]>; // axis -> excluded categories
    numeric?: Record<string, { min?: number; max?: number }>; // field -> range
  }>({});

  // Panel visibility state
  const [panelVisibility, setPanelVisibility] = useState<Record<string, boolean>>({});

  // Special plot helpers state
  const chartType = layout?.meta?.chart_type || layout?.meta?.lumi?.chart_type;
  const [volcanoSettings, setVolcanoSettings] = useState({ log2FC: 1, pValue: 0.05 });
  const [forestSettings, setForestSettings] = useState({ sortBy: 'study', highlightStudy: null as string | null });
  const [hexbinSettings, setHexbinSettings] = useState({ resolution: 20, normalize: 'auto' });

  // Detect orientation from traces
  useEffect(() => {
    const barTraces = traces.filter((t: any) => ['bar', 'box', 'violin'].includes(t.type));
    if (barTraces.length > 0) {
      const firstOrientation = barTraces[0].orientation || 'v';
      setOrientation(firstOrientation);
    }
  }, [traces]);

  // Update series visibility
  const handleSeriesVisibility = useCallback((index: number, visible: boolean) => {
    setSeriesVisibility(prev => ({ ...prev, [index]: visible }));
    onUpdate(`series.${index}.visible`, visible ? true : 'legendonly');
  }, [onUpdate]);

  // Reorder series - simplified for now (drag-and-drop can be added later)
  const handleReorderSeries = useCallback((fromIndex: number, toIndex: number) => {
    const newOrder = [...seriesOrder];
    const [removed] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, removed);
    setSeriesOrder(newOrder);
    
    // Reorder the data array
    const updatedJson = cloneFigure(plotlyJson);
    const reorderedData = newOrder.map(idx => updatedJson.data[idx]);
    
    // Apply via data update
    onUpdate('data.reorder', reorderedData);
  }, [seriesOrder, plotlyJson, onUpdate]);

  // Assign series to axis
  const handleAxisAssignment = useCallback((traceIndex: number, axis: 'y' | 'y2') => {
    const axisValue = axis === 'y' ? 'y' : 'y2';
    onUpdate(`series.${traceIndex}.yaxis`, axisValue);
  }, [onUpdate]);

  // Bar grouping mode - use helper function via onUpdate
  const handleBarMode = useCallback((mode: 'group' | 'stack' | 'percent') => {
    // Map to helper function's expected values
    const groupingMap: Record<'group' | 'stack' | 'percent', 'grouped' | 'stacked' | 'percent100'> = {
      'group': 'grouped',
      'stack': 'stacked',
      'percent': 'percent100'
    };
    
    // Use the helper function via onUpdate
    onUpdate('bar.grouping', groupingMap[mode]);
  }, [onUpdate]);

  // Orientation toggle - use helper function via onUpdate
  const handleOrientationChange = useCallback((newOrientation: 'v' | 'h') => {
    setOrientation(newOrientation);
    
    // Map 'v'/'h' to helper function's expected values
    const orientationMap: Record<'v' | 'h', 'vertical' | 'horizontal'> = {
      'v': 'vertical',
      'h': 'horizontal'
    };
    
    // Use the helper function via onUpdate
    // Note: This only works for bar traces; box/violin traces will need separate handling
    const barTraces = (plotlyJson?.data || []).filter((t: any) => t.type === 'bar');
    if (barTraces.length > 0) {
      onUpdate('bar.orientation', orientationMap[newOrientation]);
    } else {
      // For non-bar traces (box/violin), fall back to direct orientation setting
      // This is a limitation - the helper only handles bar traces
      const updatedJson = cloneFigure(plotlyJson);
      const nonBarTraces = updatedJson.data.filter((t: any) => 
        ['box', 'violin'].includes(t.type)
      );
      
      nonBarTraces.forEach((trace: any) => {
        trace.orientation = newOrientation;
      });
      
      onUpdate('data.reorder', updatedJson.data);
    }
  }, [plotlyJson, onUpdate]);

  // Category order change
  const handleCategoryOrder = useCallback((axis: 'x' | 'y', mode: string, customArray?: string[]) => {
    setCategoryOrder(prev => ({
      ...prev,
      [axis]: { mode, custom: customArray }
    }));
    
    const axisKey = axis === 'x' ? 'xaxis' : 'yaxis';
    if (mode === 'custom' && customArray) {
      onUpdate(`layout.${axisKey}.categoryorder`, 'array');
      onUpdate(`layout.${axisKey}.categoryarray`, customArray);
    } else if (mode === 'alphabetical') {
      onUpdate(`layout.${axisKey}.categoryorder`, 'category ascending');
    } else if (mode === 'ascending') {
      onUpdate(`layout.${axisKey}.categoryorder`, 'total ascending');
    } else if (mode === 'descending') {
      onUpdate(`layout.${axisKey}.categoryorder`, 'total descending');
    } else {
      onUpdate(`layout.${axisKey}.categoryorder`, 'trace');
    }
  }, [onUpdate]);

  // Histogram binning
  const handleHistogramBins = useCallback((traceIndex: number, bins: { size?: number; start?: number; end?: number; norm?: string }) => {
    setHistogramBins(prev => ({ ...prev, [traceIndex]: bins }));
    
    const trace = traces[traceIndex];
    if (trace?.type === 'histogram') {
      if (bins.size !== undefined) {
        onUpdate(`series.${traceIndex}.xbins.size`, bins.size);
      }
      if (bins.start !== undefined) {
        onUpdate(`series.${traceIndex}.xbins.start`, bins.start);
      }
      if (bins.end !== undefined) {
        onUpdate(`series.${traceIndex}.xbins.end`, bins.end);
      }
      if (bins.norm !== undefined) {
        onUpdate(`series.${traceIndex}.histnorm`, bins.norm || '');
      }
    }
  }, [traces, onUpdate]);


  // Category filter
  const handleCategoryFilter = useCallback((axis: string, excluded: string[]) => {
    setFilters(prev => ({
      ...prev,
      categories: { ...prev.categories, [axis]: excluded }
    }));
    
    // Apply filter using Plotly transforms
    const updatedJson = cloneFigure(plotlyJson);
    const axisTraces = updatedJson.data.filter((t: any) => {
      const axisKey = axis === 'x' ? 'x' : 'y';
      return Array.isArray(t[axisKey]);
    });
    
    axisTraces.forEach((trace: any) => {
      const axisKey = axis === 'x' ? 'x' : 'y';
      const values = trace[axisKey] || [];
      const keepIndices: number[] = [];
      
      values.forEach((val: any, idx: number) => {
        if (!excluded.includes(String(val))) {
          keepIndices.push(idx);
        }
      });
      
      // Filter all arrays in trace
      Object.keys(trace).forEach(key => {
        if (Array.isArray(trace[key]) && trace[key].length === values.length) {
          trace[key] = keepIndices.map(i => trace[key][i]);
        }
      });
    });
    
    // Remove traces that became empty after filtering
    const filteredTraces = updatedJson.data.filter((trace: any) => !isTraceEmpty(trace));
    
    // If we removed any traces, update the data array
    if (filteredTraces.length < updatedJson.data.length) {
      updatedJson.data = filteredTraces;
      
      // Clean up grid layout if panels were removed
      if (updatedJson.layout?.grid) {
        // Check if any panels (xaxis/yaxis combinations) are now empty
        const remainingPanels = new Set<string>();
        filteredTraces.forEach((trace: any) => {
          const xaxis = trace.xaxis || 'x';
          const yaxis = trace.yaxis || 'y';
          remainingPanels.add(`${xaxis}-${yaxis}`);
        });
        
        // If grid exists but panels were removed, we may need to adjust grid
        // Note: Plotly will handle this automatically, but we ensure consistency
        const originalPanels = new Set<string>();
        (plotlyJson.data || []).forEach((trace: any) => {
          const xaxis = trace.xaxis || 'x';
          const yaxis = trace.yaxis || 'y';
          originalPanels.add(`${xaxis}-${yaxis}`);
        });
        
        // If panels were removed, the grid will be automatically adjusted by Plotly
        // We just ensure the data is consistent
      }
    }
    
    onUpdate('data.filter', { axis, excluded, traces: updatedJson.data });
  }, [plotlyJson, onUpdate]);

  // Numeric filter
  const handleNumericFilter = useCallback((field: string, min?: number, max?: number) => {
    setFilters(prev => ({
      ...prev,
      numeric: { ...prev.numeric, [field]: { min, max } }
    }));
    
    // Apply filter - assumes field is in customdata or metadata
    const updatedJson = cloneFigure(plotlyJson);
    updatedJson.data.forEach((trace: any) => {
      if (trace.customdata && Array.isArray(trace.customdata)) {
        const keepIndices: number[] = [];
        trace.customdata.forEach((cd: any, idx: number) => {
          const val = typeof cd === 'object' ? cd[field] : cd;
          if (val !== undefined && val !== null) {
            const numVal = Number(val);
            if ((min === undefined || numVal >= min) && (max === undefined || numVal <= max)) {
              keepIndices.push(idx);
            }
          } else {
            keepIndices.push(idx); // Keep if field not present
          }
        });
        
        // Filter all arrays
        Object.keys(trace).forEach(key => {
          if (Array.isArray(trace[key]) && trace[key].length === trace.customdata.length) {
            trace[key] = keepIndices.map(i => trace[key][i]);
          }
        });
      }
    });
    
    // Remove traces that became empty after filtering
    const filteredTraces = updatedJson.data.filter((trace: any) => !isTraceEmpty(trace));
    
    // If we removed any traces, update the data array
    if (filteredTraces.length < updatedJson.data.length) {
      updatedJson.data = filteredTraces;
      
      // Clean up grid layout if panels were removed
      if (updatedJson.layout?.grid) {
        // Check if any panels (xaxis/yaxis combinations) are now empty
        const remainingPanels = new Set<string>();
        filteredTraces.forEach((trace: any) => {
          const xaxis = trace.xaxis || 'x';
          const yaxis = trace.yaxis || 'y';
          remainingPanels.add(`${xaxis}-${yaxis}`);
        });
        
        // If grid exists but panels were removed, Plotly will handle grid adjustment
        // We just ensure the data is consistent
      }
    }
    
    onUpdate('data.filterNumeric', { field, min, max, traces: updatedJson.data });
  }, [plotlyJson, onUpdate]);

  // Reset filters
  const handleResetFilters = useCallback(() => {
    setFilters({});
    if (originalFigureRef.current) {
      const restored = cloneFigure(originalFigureRef.current);
      onUpdate('data.reset', restored.data);
    }
  }, [onUpdate]);

  // Panel visibility
  const handlePanelVisibility = useCallback((panelKey: string, visible: boolean) => {
    setPanelVisibility(prev => ({ ...prev, [panelKey]: visible }));
    
    // Group traces by xaxis/yaxis
    const panelTraces = traces.filter((trace: any, idx: number) => {
      const xaxis = trace.xaxis || 'x';
      const yaxis = trace.yaxis || 'y';
      return `${xaxis}-${yaxis}` === panelKey;
    });
    
    if (!visible && panelTraces.length > 0) {
      // If hiding panel and all traces are empty, remove them instead of just hiding
      const allEmpty = panelTraces.every((trace: any) => isTraceEmpty(trace));
      
      if (allEmpty) {
        // Remove empty traces from the figure
        const updatedJson = cloneFigure(plotlyJson);
        const traceIndicesToRemove = panelTraces.map(t => traces.indexOf(t)).filter(idx => idx >= 0);
        updatedJson.data = updatedJson.data.filter((_: any, idx: number) => !traceIndicesToRemove.includes(idx));
        onUpdate('data.reorder', updatedJson.data);
        return;
      }
    }
    
    // Otherwise, just toggle visibility
    panelTraces.forEach((trace: any, idx: number) => {
      const traceIdx = traces.indexOf(trace);
      if (traceIdx >= 0) {
        onUpdate(`series.${traceIdx}.visible`, visible ? true : 'legendonly');
      }
    });
  }, [traces, plotlyJson, onUpdate]);

  // Volcano plot helpers
  const handleVolcanoUpdate = useCallback((setting: 'log2FC' | 'pValue', value: number) => {
    setVolcanoSettings(prev => ({ ...prev, [setting]: value }));
    
    // Update marker colors based on thresholds
    const updatedJson = cloneFigure(plotlyJson);
    updatedJson.data.forEach((trace: any) => {
      if (trace.customdata && Array.isArray(trace.customdata)) {
        const colors: string[] = [];
        trace.customdata.forEach((cd: any) => {
          const log2FC = cd.log2FC || cd.log2fc || 0;
          const pVal = cd.pvalue || cd.p_value || cd.p || 1;
          const negLog10P = -Math.log10(pVal);
          
          if (Math.abs(log2FC) >= volcanoSettings.log2FC && negLog10P >= -Math.log10(volcanoSettings.pValue)) {
            colors.push(log2FC > 0 ? '#ff0000' : '#0000ff'); // Red for up, blue for down
          } else {
            colors.push('#cccccc'); // Gray for non-significant
          }
        });
        
        if (!trace.marker) trace.marker = {};
        trace.marker.color = colors;
      }
    });
    
    // Add threshold lines
    if (!updatedJson.layout.shapes) updatedJson.layout.shapes = [];
    const shapes = updatedJson.layout.shapes.filter((s: any) => 
      !s.name || !s.name.startsWith('volcano_threshold_')
    );
    
    // Horizontal line at -log10(p)
    shapes.push({
      type: 'line',
      x0: 0,
      x1: 1,
      xref: 'paper',
      y0: -Math.log10(volcanoSettings.pValue),
      y1: -Math.log10(volcanoSettings.pValue),
      yref: 'y',
      line: { color: '#666', width: 1, dash: 'dash' },
      name: 'volcano_threshold_p'
    });
    
    // Vertical lines at ±log2FC
    shapes.push({
      type: 'line',
      x0: volcanoSettings.log2FC,
      x1: volcanoSettings.log2FC,
      xref: 'x',
      y0: 0,
      y1: 1,
      yref: 'paper',
      line: { color: '#666', width: 1, dash: 'dash' },
      name: 'volcano_threshold_fc_pos'
    });
    shapes.push({
      type: 'line',
      x0: -volcanoSettings.log2FC,
      x1: -volcanoSettings.log2FC,
      xref: 'x',
      y0: 0,
      y1: 1,
      yref: 'paper',
      line: { color: '#666', width: 1, dash: 'dash' },
      name: 'volcano_threshold_fc_neg'
    });
    
    updatedJson.layout.shapes = shapes;
    onUpdate('data.volcano', { traces: updatedJson.data, layout: updatedJson.layout });
  }, [plotlyJson, volcanoSettings, onUpdate]);

  // Forest plot sorting
  const handleForestSort = useCallback((sortBy: string) => {
    setForestSettings(prev => ({ ...prev, sortBy }));
    
    // Sort categories by effect size or study name
    const updatedJson = cloneFigure(plotlyJson);
    const trace = updatedJson.data[0]; // Assume single trace for forest plot
    
    if (trace && Array.isArray(trace.y)) {
      const categories = [...trace.y];
      const effectSizes = trace.x || [];
      
      let sortedCategories: string[];
      if (sortBy === 'effect_asc') {
        const indices = categories.map((_, i) => i);
        indices.sort((a, b) => (effectSizes[a] || 0) - (effectSizes[b] || 0));
        sortedCategories = indices.map(i => categories[i]);
      } else if (sortBy === 'effect_desc') {
        const indices = categories.map((_, i) => i);
        indices.sort((a, b) => (effectSizes[b] || 0) - (effectSizes[a] || 0));
        sortedCategories = indices.map(i => categories[i]);
      } else {
        sortedCategories = [...categories].sort();
      }
      
      onUpdate('layout.yaxis.categoryorder', 'array');
      onUpdate('layout.yaxis.categoryarray', sortedCategories);
    }
  }, [plotlyJson, onUpdate]);

  // Hexbin resolution
  const handleHexbinResolution = useCallback((resolution: number) => {
    setHexbinSettings(prev => ({ ...prev, resolution }));
    
    // Map resolution to bin count (approximate)
    const binCount = Math.max(5, Math.min(50, resolution));
    const updatedJson = cloneFigure(plotlyJson);
    updatedJson.data.forEach((trace: any) => {
      if (trace.type === 'histogram2d' || trace.type === 'histogram2dcontour') {
        if (!trace.xbins) trace.xbins = {};
        if (!trace.ybins) trace.ybins = {};
        trace.xbins.size = binCount;
        trace.ybins.size = binCount;
      }
    });
    
    onUpdate('data.hexbin', { traces: updatedJson.data });
  }, [plotlyJson, onUpdate]);

  // Check if secondary Y axis exists
  const hasSecondaryYAxis = useMemo(() => {
    return !!layout.yaxis2;
  }, [layout]);

  // Check if bar/area traces exist
  const hasBarTraces = useMemo(() => {
    return traces.some((t: any) => ['bar', 'histogram'].includes(t.type));
  }, [traces]);

  // Get histogram traces
  const histogramTraces = useMemo(() => {
    return traces.filter((t: any, idx: number) => t.type === 'histogram').map((t: any, idx: number) => ({
      trace: t,
      index: traces.indexOf(t)
    }));
  }, [traces]);

  // Get panels (unique xaxis/yaxis combinations) - exclude empty panels
  const panels = useMemo(() => {
    const panelMap = new Map<string, any[]>();
    traces.forEach((trace: any) => {
      // Only include non-empty traces
      if (isTraceEmpty(trace)) return;
      
      const xaxis = trace.xaxis || 'x';
      const yaxis = trace.yaxis || 'y';
      const key = `${xaxis}-${yaxis}`;
      if (!panelMap.has(key)) {
        panelMap.set(key, []);
      }
      panelMap.get(key)!.push(trace);
    });
    return Array.from(panelMap.entries())
      .filter(([_, traces]) => traces.length > 0) // Only panels with traces
      .map(([key, traces]) => ({
        key,
        traces,
        label: `Panel ${key}`
      }));
  }, [traces]);

  const [expandedSubsections, setExpandedSubsections] = useState<string[]>(['series-grouping']);

  const toggleSubsection = (id: string) => {
    setExpandedSubsections(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-lg font-semibold">Data & Encodings</h4>
      </div>

      {/* 1. Series & Grouping */}
      <Collapsible 
        open={expandedSubsections.includes('series-grouping')} 
        onOpenChange={() => toggleSubsection('series-grouping')}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent rounded-md px-2 -mx-2">
          <span className="text-sm font-medium">Series & Grouping</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 space-y-3">
          {/* Series List */}
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">Series</Label>
            {traces.map((trace: any, idx: number) => (
              <div key={idx} className="flex items-center gap-2 p-2 border rounded">
                <div className="flex-1">
                  <div className="text-sm font-medium">
                    {trace.name || `Series ${idx + 1}`}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {trace.type || 'scatter'}
                  </div>
                </div>
                <Switch
                  checked={seriesVisibility[idx] !== false}
                  onCheckedChange={(checked) => handleSeriesVisibility(idx, checked)}
                />
              </div>
            ))}
          </div>
          
          {/* Note: Drag-and-drop reordering can be added later with @dnd-kit */}

          {/* Axis Assignment */}
          {hasSecondaryYAxis && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Y-Axis Assignment</Label>
              {traces.map((trace: any, idx: number) => (
                <div key={idx} className="flex items-center justify-between">
                  <span className="text-sm">{trace.name || `Series ${idx + 1}`}</span>
                  <Select
                    value={trace.yaxis === 'y2' ? 'y2' : 'y'}
                    onValueChange={(val) => handleAxisAssignment(idx, val as 'y' | 'y2')}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="y">Left</SelectItem>
                      <SelectItem value="y2">Right</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}

          {/* Bar Grouping */}
          {hasBarTraces && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Bar Grouping</Label>
              <Select
                value={
                  layout.barnorm === 'percent' ? 'percent' :
                  layout.barmode === 'stack' ? 'stack' : 'group'
                }
                onValueChange={(val) => handleBarMode(val as 'group' | 'stack' | 'percent')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="group">Grouped</SelectItem>
                  <SelectItem value="stack">Stacked</SelectItem>
                  <SelectItem value="percent">100%</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      <Separator />

      {/* 2. Orientation & Category Order */}
      <Collapsible 
        open={expandedSubsections.includes('orientation')} 
        onOpenChange={() => toggleSubsection('orientation')}
      >
        <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent rounded-md px-2 -mx-2">
          <span className="text-sm font-medium">Orientation & Category Order</span>
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2 space-y-3">
          {/* Orientation */}
          {traces.some((t: any) => ['bar', 'box', 'violin'].includes(t.type)) && (
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Orientation</Label>
              <Select value={orientation} onValueChange={(val) => handleOrientationChange(val as 'v' | 'h')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="v">Vertical</SelectItem>
                  <SelectItem value="h">Horizontal</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Category Order */}
          {(() => {
            // Check if axes are categorical
            const xAxisType = layout.xaxis?.type || 'linear';
            const yAxisType = layout.yaxis?.type || 'linear';
            const isXCategorical = xAxisType === 'category';
            const isYCategorical = yAxisType === 'category';
            
            return (
              <>
                <div className={`space-y-2 ${!isXCategorical ? 'opacity-50' : ''}`}>
                  <Label className="text-sm text-muted-foreground">
                    X-Axis Category Order
                  </Label>
                  <Select
                    value={categoryOrder.x?.mode || 'data'}
                    onValueChange={(val) => handleCategoryOrder('x', val)}
                    disabled={!isXCategorical}
                  >
                    <SelectTrigger className={!isXCategorical ? 'cursor-not-allowed' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="data">Data Order</SelectItem>
                      <SelectItem value="alphabetical">Alphabetical</SelectItem>
                      <SelectItem value="ascending">Ascending by Value</SelectItem>
                      <SelectItem value="descending">Descending by Value</SelectItem>
                      <SelectItem value="custom">Custom Order</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className={`space-y-2 ${!isYCategorical ? 'opacity-50' : ''}`}>
                  <Label className="text-sm text-muted-foreground">
                    Y-Axis Category Order
                  </Label>
                  <Select
                    value={categoryOrder.y?.mode || 'data'}
                    onValueChange={(val) => handleCategoryOrder('y', val)}
                    disabled={!isYCategorical}
                  >
                    <SelectTrigger className={!isYCategorical ? 'cursor-not-allowed' : ''}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="data">Data Order</SelectItem>
                      <SelectItem value="alphabetical">Alphabetical</SelectItem>
                      <SelectItem value="ascending">Ascending by Value</SelectItem>
                      <SelectItem value="descending">Descending by Value</SelectItem>
                      <SelectItem value="custom">Custom Order</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            );
          })()}
        </CollapsibleContent>
      </Collapsible>

      <Separator />

      {/* 3. Binning & Histogram Options */}
      {histogramTraces.length > 0 && (
        <>
          <Collapsible 
            open={expandedSubsections.includes('binning')} 
            onOpenChange={() => toggleSubsection('binning')}
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent rounded-md px-2 -mx-2">
              <span className="text-sm font-medium">Binning & Histogram Options</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-3">
              {histogramTraces.map(({ trace, index }) => (
                <div key={index} className="space-y-3 p-2 border rounded">
                  <Label className="text-sm font-medium">
                    {trace.name || `Histogram ${index + 1}`}
                  </Label>
                  
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Bin Size</Label>
                    <Slider
                      value={[histogramBins[index]?.size || trace.xbins?.size || 1]}
                      onValueChange={([v]) => handleHistogramBins(index, { ...histogramBins[index], size: v })}
                      min={0.1}
                      max={10}
                      step={0.1}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs text-muted-foreground">Min</Label>
                      <Input
                        type="number"
                        value={histogramBins[index]?.start ?? trace.xbins?.start ?? ''}
                        onChange={(e) => handleHistogramBins(index, { 
                          ...histogramBins[index], 
                          start: e.target.value ? parseFloat(e.target.value) : undefined 
                        })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Max</Label>
                      <Input
                        type="number"
                        value={histogramBins[index]?.end ?? trace.xbins?.end ?? ''}
                        onChange={(e) => handleHistogramBins(index, { 
                          ...histogramBins[index], 
                          end: e.target.value ? parseFloat(e.target.value) : undefined 
                        })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Normalization</Label>
                    <Select
                      value={histogramBins[index]?.norm || trace.histnorm || 'none'}
                      onValueChange={(val) => handleHistogramBins(index, { 
                        ...histogramBins[index], 
                        norm: val === 'none' ? '' : val
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Counts</SelectItem>
                        <SelectItem value="probability">Probability</SelectItem>
                        <SelectItem value="percent">Percent</SelectItem>
                        <SelectItem value="density">Density</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
          <Separator />
        </>
      )}

      {/* 4. Filters & Thresholds - Only show if there are active filters */}
      {(() => {
        // Check if there are active filters
        const hasActiveCategoryFilters = Object.keys(filters.categories || {}).length > 0;
        const hasActiveNumericFilters = Object.keys(filters.numeric || {}).length > 0;
        const hasActiveFilters = hasActiveCategoryFilters || hasActiveNumericFilters;
        
        // Don't show section if there are no active filters
        if (!hasActiveFilters) {
          return null;
        }
        
        return (
          <>
            <Collapsible 
              open={expandedSubsections.includes('filters')} 
              onOpenChange={() => toggleSubsection('filters')}
            >
              <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent rounded-md px-2 -mx-2">
                <span className="text-sm font-medium">Filters & Thresholds</span>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2 space-y-3">
                <div className="text-xs text-muted-foreground mb-2">
                  Filter data points by category or numeric thresholds. Filters are applied client-side and can be reset.
                </div>
                
                {/* Category Filters */}
                {hasActiveCategoryFilters && (
                  <div className="space-y-2 p-2 border rounded">
                    <Label className="text-sm font-medium">Category Filters</Label>
                    <div className="text-xs text-muted-foreground">
                      Exclude specific categories from the plot. Use the plot's interactive selection to identify categories.
                    </div>
                  </div>
                )}
                
                {/* Numeric Filters */}
                {hasActiveNumericFilters && (
                  <div className="space-y-2 p-2 border rounded">
                    <Label className="text-sm font-medium">Numeric Filters</Label>
                    <div className="text-xs text-muted-foreground">
                      Filter by numeric fields in customdata (e.g., p-value, log2FC). Requires customdata metadata.
                    </div>
                  </div>
                )}
                
                <Button variant="outline" size="sm" onClick={handleResetFilters} className="w-full">
                  Reset All Filters
                </Button>
              </CollapsibleContent>
            </Collapsible>
            <Separator />
          </>
        );
      })()}

      {/* 5. Panels / Facets */}
      {panels.length > 1 && (
        <>
          <Collapsible 
            open={expandedSubsections.includes('panels')} 
            onOpenChange={() => toggleSubsection('panels')}
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent rounded-md px-2 -mx-2">
              <span className="text-sm font-medium">Panels / Facets</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2 space-y-3">
              {panels.map((panel) => (
                <div key={panel.key} className="flex items-center justify-between">
                  <span className="text-sm">{panel.label}</span>
                  <Switch
                    checked={panelVisibility[panel.key] !== false}
                    onCheckedChange={(checked) => handlePanelVisibility(panel.key, checked)}
                  />
                </div>
              ))}
            </CollapsibleContent>
          </Collapsible>
          <Separator />
        </>
      )}

      {/* 6. Special Plot Helpers */}
      {(chartType === 'volcano' || chartType === 'forest' || chartType === 'hexbin') && (
        <Collapsible 
          open={expandedSubsections.includes('special')} 
          onOpenChange={() => toggleSubsection('special')}
        >
          <CollapsibleTrigger className="flex items-center justify-between w-full py-2 hover:bg-accent rounded-md px-2 -mx-2">
            <span className="text-sm font-medium">Special Plot Helpers</span>
          </CollapsibleTrigger>
          <CollapsibleContent className="pt-2 space-y-3">
            {chartType === 'volcano' && (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">Log2FC Threshold</Label>
                  <Slider
                    value={[volcanoSettings.log2FC]}
                    onValueChange={([v]) => handleVolcanoUpdate('log2FC', v)}
                    min={0}
                    max={5}
                    step={0.1}
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm text-muted-foreground">p-value Threshold</Label>
                  <Input
                    type="number"
                    value={volcanoSettings.pValue}
                    onChange={(e) => handleVolcanoUpdate('pValue', parseFloat(e.target.value) || 0.05)}
                    min={0}
                    max={1}
                    step={0.01}
                  />
                </div>
              </div>
            )}

            {chartType === 'forest' && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Sort By</Label>
                <Select
                  value={forestSettings.sortBy}
                  onValueChange={(val) => handleForestSort(val)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="study">Study Name</SelectItem>
                    <SelectItem value="effect_asc">Effect Size (Ascending)</SelectItem>
                    <SelectItem value="effect_desc">Effect Size (Descending)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {chartType === 'hexbin' && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Bin Resolution</Label>
                <Slider
                  value={[hexbinSettings.resolution]}
                  onValueChange={([v]) => handleHexbinResolution(v)}
                  min={5}
                  max={50}
                  step={1}
                />
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>
      )}
    </div>
  );
}



